import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { animate, motion, useReducedMotion } from 'framer-motion';
import { TONES, Tone } from '../../../components/ui/PageKit';

// Piezas visuales del plano: el naranja de Parriplast, números que cuentan, puntos que laten y el panel lateral.

export const ACCENT = '#F0561D';
export const ACCENT_DARK = '#C9420F';
export const ACCENT_SOFT = '#FFF1EA';
export const INK = '#16181D';
export const MUTED = '#6B7280';
export const LINE = '#ECEAE6';
export const SURFACE = '#F7F6F3';

const nf = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Número que cuenta hasta su valor cuando aparece o cambia. */
export const AnimatedNumber: React.FC<{ value: number | null | undefined; decimals?: 0 | 1; prefix?: string; suffix?: string; duration?: number }> = ({
  value, decimals = 0, prefix = '', suffix = '', duration = 0.9,
}) => {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(value ?? 0);
  const from = useRef(0);
  useEffect(() => {
    if (value == null) return;
    if (reduce) { setShown(value); from.current = value; return; }
    const ctrl = animate(from.current, value, { duration, ease: [0.16, 1, 0.3, 1], onUpdate: v => setShown(v) });
    from.current = value;
    return () => ctrl.stop();
  }, [value, reduce, duration]);
  if (value == null) return <>—</>;
  return <>{prefix}{decimals ? nf1.format(shown) : nf.format(Math.round(shown))}{suffix}</>;
};

/** Punto de estado; si está activo o en rojo, late. */
export const PulseDot: React.FC<{ tone: Tone; size?: number; pulse?: boolean }> = ({ tone, size = 10, pulse }) => {
  const reduce = useReducedMotion();
  const color = TONES[tone].dot;
  return (
    <Box component="span" sx={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0 }}>
      {pulse && !reduce && (
        <motion.span
          style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: color }}
          initial={{ opacity: 0.55, scale: 1 }}
          animate={{ opacity: 0, scale: 2.6 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
        />
      )}
      <Box component="span" sx={{ position: 'relative', width: size, height: size, borderRadius: '50%', bgcolor: color, boxShadow: '0 0 0 2px #fff' }} />
    </Box>
  );
};

/** Barra de avance que crece al aparecer. */
export const ProgressBar: React.FC<{ value: number; color?: string; height?: number }> = ({ value, color = ACCENT, height = 8 }) => {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <Box sx={{ height, borderRadius: 999, bgcolor: '#ECEBE8', overflow: 'hidden' }}>
      <motion.div
        style={{ height: '100%', borderRadius: 999, background: color }}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      />
    </Box>
  );
};

/** Barritas por día (últimos 7). */
export const MiniBars: React.FC<{ values: { label: string; value: number }[]; color?: string; height?: number }> = ({ values, color = ACCENT, height = 64 }) => {
  const max = Math.max(1, ...values.map(v => v.value));
  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ height: height + 18 }}>
      {values.map((v, i) => (
        <Stack key={v.label} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ height, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
            <motion.div
              title={`${v.label}: ${nf.format(v.value)}`}
              style={{ width: '100%', borderRadius: 4, background: v.value ? color : '#E4E2DE' }}
              initial={{ height: 0 }}
              animate={{ height: Math.max(3, (v.value / max) * height) }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
            />
          </Box>
          <Typography sx={{ fontSize: 10.5, color: MUTED, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>{v.label}</Typography>
        </Stack>
      ))}
    </Stack>
  );
};

/** Cifra grande del panel (82.000 Disponibles). */
export const HeroFigure: React.FC<{ value: number | null | undefined; unit?: string; caption: string; tone?: 'accent' | Tone; decimals?: 0 | 1 }> = ({
  value, unit, caption, tone = 'accent', decimals,
}) => {
  const fg = tone === 'accent' ? ACCENT : TONES[tone].fg;
  const bg = tone === 'accent' ? ACCENT_SOFT : TONES[tone].bg;
  return (
    <Box sx={{ bgcolor: bg, borderRadius: 2.5, px: 2.5, py: 2, minWidth: 0 }}>
      <Typography sx={{ fontSize: { xs: 38, md: 46 }, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1, color: fg, fontVariantNumeric: 'tabular-nums' }}>
        <AnimatedNumber value={value} decimals={decimals} />{unit && <Box component="span" sx={{ fontSize: '0.5em', ml: 0.75, fontWeight: 700 }}>{unit}</Box>}
      </Typography>
      <Typography sx={{ mt: 0.75, fontWeight: 700, color: fg, fontSize: 15 }}>{caption}</Typography>
    </Box>
  );
};

/** Dato chico en caja gris (Stock físico 100.000). */
export const StatBox: React.FC<{ label: React.ReactNode; value: React.ReactNode; tone?: Tone; hint?: React.ReactNode }> = ({ label, value, tone, hint }) => (
  <Box sx={{ bgcolor: tone ? TONES[tone].bg : SURFACE, borderRadius: 2, px: 1.75, py: 1.25, minWidth: 0 }}>
    <Typography sx={{ fontSize: 12.5, color: tone ? TONES[tone].fg : MUTED }}>{label}</Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 750, letterSpacing: '-0.02em', color: tone ? TONES[tone].fg : INK, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
      {value}
    </Typography>
    {hint && <Typography sx={{ fontSize: 11.5, color: MUTED }}>{hint}</Typography>}
  </Box>
);

export const PanelTitle: React.FC<{ children: React.ReactNode; action?: React.ReactNode }> = ({ children, action }) => (
  <Stack direction="row" alignItems="center" sx={{ mt: 3, mb: 1.25 }}>
    <Typography sx={{ fontSize: 17, fontWeight: 750, letterSpacing: '-0.015em', flex: 1 }}>{children}</Typography>
    {action}
  </Stack>
);

/** Aviso con ícono a la izquierda (Stock suficiente / Diferencia pendiente). */
export const Callout: React.FC<{ tone: Tone; icon?: React.ReactNode; title: React.ReactNode; children?: React.ReactNode }> = ({ tone, icon, title, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ bgcolor: TONES[tone].bg, borderRadius: 2, px: 2, py: 1.5, mt: 2 }}>
    {icon && <Box sx={{ color: TONES[tone].dot, display: 'flex', fontSize: 30 }}>{icon}</Box>}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, color: TONES[tone].fg }}>{title}</Typography>
      {children && <Typography variant="body2" sx={{ color: MUTED }}>{children}</Typography>}
    </Box>
  </Stack>
);

/** Panel lateral: título grande, bajada y botón de cerrar; el cuerpo entra deslizando. */
export const PanelShell: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  badge?: React.ReactNode;
  onClose?: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, badge, onClose, footer, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
    <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.75 }, pb: 1 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
            <Typography component="h2" sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}>{title}</Typography>
            {badge}
          </Stack>
          {subtitle && <Typography sx={{ color: MUTED, mt: 0.5, fontSize: 15 }}>{subtitle}</Typography>}
        </Box>
        {onClose && <IconButton aria-label="Cerrar" onClick={onClose} sx={{ mt: -0.5, mr: -1 }}><Close /></IconButton>}
      </Stack>
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, md: 3 }, pb: 2 }}>{children}</Box>
    {footer && (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: `1px solid ${LINE}`, bgcolor: '#fff' }}>{footer}</Box>
    )}
  </Box>
);

/** Lista que entra escalonada. */
export const Stagger: React.FC<{ children: React.ReactNode; gap?: number }> = ({ children, gap = 1 }) => (
  <Stack spacing={gap}>
    {React.Children.toArray(children).map((c, i) => (
      <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: Math.min(i, 10) * 0.04 }}>
        {c}
      </motion.div>
    ))}
  </Stack>
);

export const accentButton = {
  bgcolor: ACCENT, color: '#fff', fontWeight: 700, py: 1.25, borderRadius: 2, boxShadow: 'none', textTransform: 'none', fontSize: 15,
  '&:hover': { bgcolor: ACCENT_DARK, boxShadow: 'none' },
} as const;

export const outlineButton = {
  color: ACCENT, borderColor: ACCENT, fontWeight: 700, py: 1.25, borderRadius: 2, textTransform: 'none', fontSize: 15, borderWidth: 1.5,
  '&:hover': { borderColor: ACCENT_DARK, bgcolor: ACCENT_SOFT, borderWidth: 1.5 },
} as const;
